import React from 'react';
import { formatArizonaTimestamp } from '../../utils/guardianEncounterDeadline';
import {
  DAILY_NOTE_NOT_STORED,
  DailyNoteGoal,
  formatDailyNoteGoals,
  mapDailyTreatmentNote
} from '../../utils/dailyTreatmentNote';
import { soapRevisionDisplay } from '../../utils/soapEncounterAcknowledgment';

export const DailyTreatmentNoteBody: React.FC<{
  doc: any;
  light?: boolean;
  editing?: {
    narrative: string;
    goals: DailyNoteGoal[];
    observations: string;
    onNarrative: (value: string) => void;
    onGoals: (goals: DailyNoteGoal[]) => void;
    onObservations: (value: string) => void;
  };
}> = ({ doc, light = false, editing }) => {
  const note = mapDailyTreatmentNote(doc);
  const revision = soapRevisionDisplay(doc);
  const label = light ? 'text-slate-800' : 'text-slate-300';
  const box = light
    ? 'bg-slate-50 border border-slate-200/90 text-slate-800'
    : 'bg-[#0a101b] border border-slate-700/80 text-slate-100';
  const applicableSupervisor = Boolean(
    doc?.provider_role === 'SLPA' ||
    doc?.provider_role === 'ASSISTANT' ||
    doc?.supervisor_name ||
    doc?.supervisor_signature ||
    doc?.supervisor_signed_at
  );

  const block = (title: string, body: string) => (
    <div className="space-y-1.5 break-inside-avoid">
      <div className={`text-[11px] font-bold uppercase tracking-wider print:text-slate-950 ${label}`}>{title}</div>
      <div className={`p-3.5 rounded-xl border leading-relaxed whitespace-pre-wrap select-text print:bg-white print:border-slate-300 print:text-slate-900 ${box}`}>
        {body}
      </div>
    </div>
  );

  const fieldClass = `w-full rounded-xl p-3 text-xs leading-relaxed focus:outline-none ${box}`;
  const updateGoal = (index: number, patch: Partial<DailyNoteGoal>) => {
    if (!editing) return;
    editing.onGoals(editing.goals.map((goal, goalIndex) => goalIndex === index ? { ...goal, ...patch } : goal));
  };

  return (
    <div className="space-y-4 text-xs print:text-slate-900">
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-xl border print:bg-white print:border-slate-300 ${box}`}>
        <div>Document ID: {note.documentId}</div>
        <div>Date of service: {note.session.dateOfService}</div>
        <div>Time in: {note.session.timeIn}</div>
        <div>Time out: {note.session.timeOut}</div>
        <div>Duration (minutes): {note.session.durationMinutes}</div>
        <div>Service code: {note.session.serviceCode}</div>
        <div>Place of service: {note.session.placeOfService}</div>
        <div>Clients treated together: {note.session.clientsTreatedTogether}</div>
        <div>Provider: {doc?.provider_name || DAILY_NOTE_NOT_STORED}{doc?.provider_title ? `, ${doc.provider_title}` : ''}</div>
        <div>Member: {`${doc?.first_name || ''} ${doc?.last_name || ''}`.trim() || DAILY_NOTE_NOT_STORED}</div>
      </div>
      {revision.revised && (
        <div className={`p-3 rounded-xl border leading-relaxed ${box}`}>
          <div className={`text-[11px] font-bold uppercase tracking-wider ${label}`}>Current clinical revision</div>
          <p className="mt-1">{revision.statement}</p>
          {revision.fields.length > 0 && <p className="mt-1">Revised fields: {revision.fields.join(', ')}</p>}
        </div>
      )}
      {editing ? (
        <div className="space-y-1.5 break-inside-avoid">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${label}`}>{revision.narrativeTitle}</div>
          <textarea rows={6} value={editing.narrative} onChange={(event) => editing.onNarrative(event.target.value)} className={fieldClass} />
        </div>
      ) : block(revision.narrativeTitle, note.narrative)}
      {editing ? (
        <div className="space-y-2 break-inside-avoid">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${label}`}>Targeted goals and performance</div>
          {editing.goals.map((goal, index) => (
            <div key={`${goal.goalId || 'goal'}-${index}`} className={`p-3 rounded-xl border space-y-2 ${box}`}>
              <input value={goal.description} onChange={(event) => updateGoal(index, { description: event.target.value })} className={fieldClass} placeholder="Goal" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input value={goal.accuracy === DAILY_NOTE_NOT_STORED ? '' : goal.accuracy} onChange={(event) => updateGoal(index, { accuracy: event.target.value })} className={fieldClass} placeholder="Accuracy" />
                <input value={goal.cueing === DAILY_NOTE_NOT_STORED ? '' : goal.cueing} onChange={(event) => updateGoal(index, { cueing: event.target.value })} className={fieldClass} placeholder="Cueing" />
                <input value={goal.evidence === DAILY_NOTE_NOT_STORED ? '' : goal.evidence} onChange={(event) => updateGoal(index, { evidence: event.target.value })} className={fieldClass} placeholder="Trial evidence" />
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => editing.onGoals([...editing.goals, { description: '', accuracy: '', cueing: '', evidence: '' }])}
            className="text-xs font-semibold underline"
          >
            Add goal
          </button>
        </div>
      ) : block('Targeted goals and performance', formatDailyNoteGoals(note.goals))}
      {editing ? (
        <div className="space-y-1.5 break-inside-avoid">
          <div className={`text-[11px] font-bold uppercase tracking-wider ${label}`}>Additional clinical observations</div>
          <textarea rows={5} value={editing.observations} onChange={(event) => editing.onObservations(event.target.value)} className={fieldClass} />
        </div>
      ) : block('Additional clinical observations', note.observations.join('\n\n') || DAILY_NOTE_NOT_STORED)}
      {block('Clinician signature', `${doc?.clinician_signature || DAILY_NOTE_NOT_STORED}${doc?.clinician_signed_at ? ` — ${formatArizonaTimestamp(doc.clinician_signed_at)}` : ''}`)}
      {block(revision.guardianTitle, `${doc?.guardian_signature || DAILY_NOTE_NOT_STORED}${doc?.guardian_relationship ? ` (${doc.guardian_relationship})` : ''}${doc?.guardian_signed_at ? ` — ${formatArizonaTimestamp(doc.guardian_signed_at)}` : ''}${revision.revised ? `\n${revision.statement}` : ''}`)}
      {applicableSupervisor && block('Supervisor', `${doc?.supervisor_signature || doc?.supervisor_name || DAILY_NOTE_NOT_STORED}${doc?.supervisor_signed_at ? ` — ${formatArizonaTimestamp(doc.supervisor_signed_at)}` : ''}${doc?.supervisor_approval_status ? ` — ${doc.supervisor_approval_status}` : ''}`)}
    </div>
  );
};
